import React, { Dispatch, SetStateAction } from "react";

import { getDamPath } from "@utils/configuration/aem";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import CalculationDropDownField from "@components/Calculator/CalculationDropDownField/calculationDropDownField.component";
import CalculationField from "@components/Calculator/CalculationField/calculationField.component";
import { Input } from "@components/General/Form/input.component";
import { Label } from "@components/General/Form/label.component";
import { Col } from "@components/General/Grid/col.component";
import { Row } from "@components/General/Grid/row.component";

import {
    OndulineColor,
    ProductLine,
    ProductLineType,
} from "../corrugatedSheetCalculator.types";
import { CorrugatedSheetsCalculationConfiguration } from "../corrugatedSheetsCalculation.config";
import "./calculation.scss";

type CalculationComponentProps = {
    setHeight: Dispatch<SetStateAction<string>>;
    setWidth: Dispatch<SetStateAction<string>>;
    setLength: Dispatch<SetStateAction<string>>;
    setProductLine: Dispatch<SetStateAction<ProductLineType>>;
    setIsTwoSidedRoof: Dispatch<SetStateAction<boolean>>;
    setColor: Dispatch<SetStateAction<OndulineColor | undefined>>;
    height: string;
    width: string;
    length: string;
    productLine: ProductLineType;
    isTwoSidedRoof: boolean;
    color?: OndulineColor;
};

const CalculationComponent = ({
    setHeight,
    setLength,
    setWidth,
    setProductLine,
    setIsTwoSidedRoof,
    setColor,
    height,
    length,
    width,
    productLine,
    isTwoSidedRoof,
    color,
}: CalculationComponentProps) => {
    const { t } = useGranite();

    return (
        <div className={"mt-4"}>
            <Row>
                <Col md={6}>
                    <h3 className="d-sm-block">
                        {t(
                            "calculator.corrugatedSheetCalculator.corrugatedSheetMeasurements",
                        )}
                    </h3>
                    <CalculationField
                        classNames="pl-0"
                        md={6}
                        name={"corrugatedSheetHeight"}
                        dimension={"height"}
                        unit={"cm"}
                        setCalculationValue={(_: string, value: string) =>
                            setHeight(value)
                        }
                        value={height}
                    />
                    <CalculationField
                        classNames="pl-0"
                        md={6}
                        name={"corrugatedSheetWidth"}
                        dimension={"width"}
                        unit={"cm"}
                        setCalculationValue={(_: string, value: string) =>
                            setWidth(value)
                        }
                        value={width}
                    />
                    <CalculationField
                        classNames="pl-0"
                        md={6}
                        name={"corrugatedSheetLength"}
                        dimension={"length"}
                        unit={"cm"}
                        setCalculationValue={(_: string, value: string) =>
                            setLength(value)
                        }
                        value={length}
                    />
                </Col>
                <Col md={6}>
                    <img
                        className={"mt-5 calculator-image--large"}
                        src={`${getDamPath()}/hulp-en-advies/calculators/calculator-corrugated-sheets.png`}
                        alt={"calculator-corrugated-sheets.png"}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={6} className="mt-3">
                    <Label for="two-sided" check className="calculation-radio">
                        <Input
                            type="checkbox"
                            id="two-sided"
                            checked={isTwoSidedRoof}
                            onChange={e => {
                                setIsTwoSidedRoof(
                                    (e.target as HTMLInputElement).checked,
                                );
                            }}
                        />{" "}
                        {t("calculator.corrugatedSheetCalculator.isTwoSided")}
                    </Label>
                </Col>
            </Row>
            <Row>
                <CalculationDropDownField
                    md={6}
                    values={Object.keys(ProductLine).map(value => ({
                        value,
                        label: t(
                            `calculator.corrugatedSheetCalculator.productLine.${value}`,
                        ),
                    }))}
                    name={"corrugatedSheetProductLine"}
                    dimension={"corrugatedSheets.productLine"}
                    setCalculationValue={(_: string, value: string) =>
                        setProductLine(value as ProductLineType)
                    }
                    value={productLine}
                />
            </Row>
            <Row>
                <CalculationDropDownField
                    md={6}
                    values={
                        CorrugatedSheetsCalculationConfiguration[
                            productLine
                        ].sheets?.map(value => ({
                            value: value.color.toString(),
                            label: t(
                                `calculator.corrugatedSheetCalculator.productLine.color.${value.color}`,
                            ),
                        })) || []
                    }
                    name={"corrugatedSheetProductLineColor"}
                    dimension={"corrugatedSheets.productLineColor"}
                    setCalculationValue={(_: string, value: string) =>
                        setColor(value as unknown as OndulineColor)
                    }
                    value={color}
                />
            </Row>
        </div>
    );
};

export default CalculationComponent;
